import type { TokenUsdPriceData } from "@src/components/DefuseSDK/hooks/useTokensUsdPrices"
import type { BaseTokenInfo, TokenInfo } from "../types/base"
import { isBaseToken, isUnifiedToken } from "./token"

/** Price API keys omit the `1cs_v1:<chain>:` routing prefix used by derived tokens. */
export function priceLookupAssetIds(defuseAssetId: string): string[] {
  const ids = [defuseAssetId]
  if (defuseAssetId.startsWith("1cs_v1:")) {
    const withoutPrefix = defuseAssetId.slice("1cs_v1:".length)
    const chainSeparator = withoutPrefix.indexOf(":")
    if (chainSeparator !== -1) {
      ids.push(withoutPrefix.slice(chainSeparator + 1))
    }
  }
  return ids
}

function lookupPriceByAssetId(
  defuseAssetId: string,
  priceData: TokenUsdPriceData
): number | null {
  for (const assetId of priceLookupAssetIds(defuseAssetId)) {
    const price = priceData[assetId]?.price
    if (price != null && price > 0) return price
  }
  return null
}

function lookupOverrideByAssetId(
  defuseAssetId: string,
  overrides: Record<string, number>
): number | null {
  for (const assetId of priceLookupAssetIds(defuseAssetId)) {
    const price = overrides[assetId]
    if (price != null) return price
  }
  return null
}

function lookupOverride(
  token: TokenInfo,
  overrides: Record<string, number>
): number | null {
  if (isBaseToken(token)) {
    return lookupOverrideByAssetId(token.defuseAssetId, overrides)
  }

  if (isUnifiedToken(token)) {
    for (const grouped of token.groupedTokens) {
      const price = lookupOverrideByAssetId(grouped.defuseAssetId, overrides)
      if (price != null) return price
    }
  }

  return null
}

export function getTokenPrice(
  token: TokenInfo | null,
  priceData?: TokenUsdPriceData,
  overrides?: Record<string, number>
): number | null {
  if (!token) return null

  if (overrides) {
    const override = lookupOverride(token, overrides)
    if (override != null) return override
  }

  if (!priceData) return null

  if (isBaseToken(token)) {
    return lookupPriceByAssetId(token.defuseAssetId, priceData)
  }

  if (isUnifiedToken(token)) {
    for (const grouped of token.groupedTokens) {
      if (!isBaseToken(grouped)) continue
      const price = lookupPriceByAssetId(grouped.defuseAssetId, priceData)
      if (price != null) return price
    }
  }

  return null
}

/**
 * Inverse of {@link getTokenUsdPrice}: how much of `token` is worth `usdValue`,
 * formatted to the token's decimals so it can be parsed with `parseUnits`.
 * Returns null when no price is available or the result rounds to zero.
 */
export function usdAmountToTokenAmountString(
  usdValue: number,
  token: BaseTokenInfo | TokenInfo | null,
  priceData?: TokenUsdPriceData
): string | null {
  const price = getTokenPrice(token, priceData)
  if (token == null || price == null || price <= 0) return null

  const decimals = isBaseToken(token)
    ? token.decimals
    : token.groupedTokens.find(isBaseToken)?.decimals
  if (decimals == null) return null

  const tokenAmount = (usdValue / price).toFixed(decimals)
  // Guard against rounding to 0 for high-priced tokens with few decimals.
  return Number(tokenAmount) > 0 ? tokenAmount : null
}

const getTokenUsdPrice = (
  tokenAmount: string,
  token: TokenInfo | null,
  tokensUsdPriceData?: TokenUsdPriceData,
  overrides?: Record<string, number>
): number | null => {
  try {
    if (!token || !tokenAmount) return null
    const numberTokenAmount = +tokenAmount
    if (Number.isNaN(numberTokenAmount) || !Number.isFinite(numberTokenAmount))
      return null
    const price = getTokenPrice(token, tokensUsdPriceData, overrides)
    if (price == null) return null
    return numberTokenAmount * price
  } catch {
    return null
  }
}
export default getTokenUsdPrice
